import { useState, useMemo, useCallback, useLayoutEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Tabs,
  Tab,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Divider,
  Menu,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Add,
  Delete,
  DriveFileMove,
  AccountTree,
  ArrowUpward,
  ArrowDownward,
  Business,
  LocationOn,
  Warehouse,
  Groups,
  Domain,
} from '@mui/icons-material';
import { RichTreeView } from '@mui/x-tree-view/RichTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { useOrgUnits, useCreateOrgUnit, useUpdateOrgUnit, useDeleteOrgUnit } from '@/hooks/core/useOrgUnit';
import { OrgUnit, CreateOrgUnit, UpdateOrgUnit } from '@/types/core/OrgUnit';
import { ViewToolbar } from '@/components/ViewToolbar';
import { saveShortcutLabel, cancelShortcutLabel } from '@/hooks/useSaveShortcut';
import { useResizablePanelWidth } from '@/components/useResizablePanelWidth';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { JsonFormsThemeProvider } from '@/components/forms/JsonFormsTheme';
import { ValidationErrorBanner } from '@/components/forms/ValidationErrorBanner';
import { ValidationErrorPanel } from '@/components/forms/ValidationErrorPanel';
import { ValidationError, getValidationErrors } from '@/lib/validationErrors';
import { OrgUnitMembersTab } from '@/core/OrgUnitMembersTab';
import { MoveOrgUnitDialog } from '@/core/MoveOrgUnitDialog';
import { buildOrgUnitUpdatePayload } from '@/core/orgUnitUpdatePayload';
import { useReorderOrgUnits } from '@/core/useReorderOrgUnits';
import { compareTreeSiblings } from '@/lib/treeLookup';
import { PrincipalPicker } from '@/components/forms/PrincipalPicker';
import { DimensionValuesEditor } from '@/components/forms/DimensionValuesEditor';
import { apiClient } from '@/lib/apiClient';

// --- Constants ---

const ORG_UNIT_TYPES = [
  'LEGAL_ENTITY',
  'BUSINESS_UNIT',
  'SITE',
  'WAREHOUSE',
  'DEPARTMENT',
  'GROUP',
] as const;

const TYPE_LABELS: Record<string, string> = {
  LEGAL_ENTITY: 'Legal Entity',
  BUSINESS_UNIT: 'Business Unit',
  SITE: 'Site',
  WAREHOUSE: 'Warehouse',
  DEPARTMENT: 'Department',
  GROUP: 'Group',
};

const TYPE_ICONS: Record<string, typeof Business> = {
  LEGAL_ENTITY: Domain,
  BUSINESS_UNIT: Business,
  SITE: LocationOn,
  WAREHOUSE: Warehouse,
  DEPARTMENT: Groups,
  GROUP: AccountTree,
};

// --- Types ---

interface OrgUnitTreeItem {
  id: string;
  label: string;
  orgUnit: OrgUnit;
  children?: OrgUnitTreeItem[];
}

// --- Helpers ---

function getTypeIcon(type: string) {
  const Icon = TYPE_ICONS[type] || AccountTree;
  return <Icon fontSize="small" sx={{ mr: 0.5 }} />;
}

/** Build tree structure from flat OrgUnit array */
function buildTree(orgUnits: OrgUnit[]): OrgUnitTreeItem[] {
  const map = new Map<string, OrgUnitTreeItem>();
  const roots: OrgUnitTreeItem[] = [];

  // Create nodes
  for (const ou of orgUnits) {
    map.set(ou.id, {
      id: ou.id,
      label: ou.name,
      orgUnit: ou,
      children: [],
    });
  }

  // Build hierarchy
  for (const ou of orgUnits) {
    const node = map.get(ou.id)!;
    if (ou.parentId && map.has(ou.parentId)) {
      map.get(ou.parentId)!.children!.push(node);
    } else {
      roots.push(node);
    }
  }

  // Order every sibling group
  const sortRecursive = (nodes: OrgUnitTreeItem[]) => {
    nodes.sort((a, b) => compareTreeSiblings(a.orgUnit, b.orgUnit));
    for (const node of nodes) {
      if (node.children?.length) sortRecursive(node.children);
    }
  };
  sortRecursive(roots);

  return roots;
}

/** The siblings of `orgUnit` (including itself), in display order. */
function getSiblingsInOrder(orgUnits: OrgUnit[], orgUnit: OrgUnit): OrgUnit[] {
  return orgUnits
    .filter((ou) => (ou.parentId ?? null) === (orgUnit.parentId ?? null))
    .sort(compareTreeSiblings);
}

// --- Detail Panel (presentational; edit state + Save/Cancel live in the page) ---

interface DetailPanelProps {
  orgUnit: OrgUnit;
  formData: UpdateOrgUnit;
  setFormData: Dispatch<SetStateAction<UpdateOrgUnit | null>>;
  // Owned by the page (not local state): DetailPanel remounts per node (key={orgUnit.id}), and the
  // selected tab must survive a node switch — browsing members unit by unit stays on Members.
  tabIndex: number;
  setTabIndex: (value: number) => void;
  // Bumped by Cancel: the DimensionValuesEditor stages its picks internally, so discarding them
  // means remounting it (its key includes this counter).
  dimensionResetKey: number;
  onDimensionChange: (map: Record<string, string | null>) => void;
}

function DetailPanel({ orgUnit, formData, setFormData, tabIndex, setTabIndex, dimensionResetKey, onDimensionChange }: DetailPanelProps) {
  const { t } = useTranslation();

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        {getTypeIcon(orgUnit.type)}
        <Typography variant="h6">{orgUnit.name}</Typography>
        <Chip
          label={TYPE_LABELS[orgUnit.type] || orgUnit.type}
          size="small"
          color="primary"
          variant="outlined"
        />
        {orgUnit.isActive ? (
          <Chip label={t('common.active')} size="small" color="success" />
        ) : (
          <Chip label={t('common.inactive')} size="small" color="default" />
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ px: 3, pb: 1 }}>
        Code: {orgUnit.code}
        {orgUnit.timezone && ` · ${orgUnit.timezone}`}
        {orgUnit.country && ` · ${orgUnit.country}`}
      </Typography>

      <Divider />

      {/* Tabs */}
      <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ px: 3 }}>
        <Tab label={t('orgUnit.details')} />
        <Tab label={t('orgUnit.dimensions', { defaultValue: 'Dimensions' })} />
        <Tab label={t('orgUnit.members')} />
      </Tabs>

      {/* Tab content */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
        {tabIndex === 0 && (
          <JsonFormsThemeProvider density="compact" variant="standard">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 480 }}>
              <TextField
                label={t('common.name')}
                value={formData.name}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, name: e.target.value }))}
                fullWidth
                size="small"
              />
              <TextField
                label={t('common.code')}
                value={formData.code}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, code: e.target.value }))}
                fullWidth
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>{t('common.type')}</InputLabel>
                <Select
                  value={formData.type}
                  label={t('common.type')}
                  onChange={(e) => setFormData((prev: any) => ({ ...prev, type: e.target.value }))}
                >
                  {ORG_UNIT_TYPES.map((typ) => (
                    <MenuItem key={typ} value={typ}>{TYPE_LABELS[typ]}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label={t('common.timezone')}
                value={formData.timezone || ''}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, timezone: e.target.value || undefined }))}
                fullWidth
                size="small"
                placeholder="e.g., Europe/Stockholm"
              />
              <TextField
                label={t('common.country')}
                value={formData.country || ''}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, country: e.target.value || undefined }))}
                fullWidth
                size="small"
              />
              <TextField
                label={t('orgUnit.sortOrder')}
                type="number"
                value={formData.sortOrder}
                onChange={(e) => setFormData((prev: any) => ({ ...prev, sortOrder: Number(e.target.value) || 0 }))}
                fullWidth
                size="small"
                helperText={t('orgUnit.sortOrderHint')}
              />

              <Divider sx={{ my: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                {t('orgUnit.responsibility', { defaultValue: 'Responsibility' })}
              </Typography>
              <PrincipalPicker
                label={t('orgUnit.owner', { defaultValue: 'Owner' })}
                defaultNodeId={orgUnit.id}
                value={{
                  type: formData.ownerPrincipalType,
                  id: formData.ownerPrincipalId,
                  qualifier: formData.ownerPrincipalQualifier,
                }}
                onChange={(v) => setFormData((prev: any) => ({
                  ...prev,
                  ownerPrincipalType: v.type,
                  ownerPrincipalId: v.id,
                  ownerPrincipalQualifier: v.qualifier,
                }))}
              />
            </Box>
          </JsonFormsThemeProvider>
        )}

        {tabIndex === 1 && (
          <JsonFormsThemeProvider density="compact" variant="standard">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 560 }}>
              {/* key: remount per node so the editor's pre-fill state never leaks across selections,
                  and per reset so Cancel discards staged picks */}
              <DimensionValuesEditor
                key={`${orgUnit.id}:${dimensionResetKey}`}
                module="core"
                dimensionSetId={orgUnit.defaultDimensionSetId}
                inheritFromNodeId={orgUnit.parentId}
                inheritSource="org-unit"
                entity="OrgUnit"
                label={t('orgUnit.dimensions', { defaultValue: 'Dimensions' })}
                description={t('orgUnit.dimensionsHint', {
                  defaultValue:
                    'Default dimension values for plans and budgets owned by this unit. Grayed values are inherited from the parent chain; pick a value to override for this unit.',
                })}
                onChange={onDimensionChange}
              />
            </Box>
          </JsonFormsThemeProvider>
        )}

        {tabIndex === 2 && (
          <OrgUnitMembersTab orgUnitId={orgUnit.id} />
        )}
      </Box>
    </Box>
  );
}

// --- Create Dialog ---

interface CreateDialogProps {
  open: boolean;
  parentId?: string;
  parentName?: string;
  onClose: () => void;
  onCreate: (data: CreateOrgUnit) => void;
  isCreating: boolean;
}

function CreateOrgUnitDialog({ open, parentId, parentName, onClose, onCreate, isCreating }: CreateDialogProps) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<CreateOrgUnit>({
    type: 'LEGAL_ENTITY',
    code: '',
    name: '',
    isActive: true,
    parentId,
    // 0 = "not specified"; the backend places the new unit after its siblings.
    sortOrder: 0,
  });

  // Reset when dialog opens
  useMemo(() => {
    if (open) {
      setFormData({
        type: parentId ? 'BUSINESS_UNIT' : 'LEGAL_ENTITY',
        code: '',
        name: '',
        isActive: true,
        parentId,
        sortOrder: 0,
      });
    }
  }, [open, parentId]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {parentId ? t('orgUnit.addChildUnder', { parent: parentName || 'selected unit' }) : t('orgUnit.createRootOrgUnit')}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        <TextField
          label={t('common.name')}
          value={formData.name}
          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
          fullWidth
          size="small"
          autoFocus
          required
        />
        <TextField
          label={t('common.code')}
          value={formData.code}
          onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
          fullWidth
          size="small"
          required
        />
        <FormControl fullWidth size="small">
          <InputLabel>{t('common.type')}</InputLabel>
          <Select
            value={formData.type}
            label={t('common.type')}
            onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value }))}
          >
            {ORG_UNIT_TYPES.map((typ) => (
              <MenuItem key={typ} value={typ}>{TYPE_LABELS[typ]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label={t('common.timezone')}
          value={formData.timezone || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, timezone: e.target.value || undefined }))}
          fullWidth
          size="small"
          placeholder="e.g., Europe/Stockholm"
        />
        <TextField
          label={t('common.country')}
          value={formData.country || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value || undefined }))}
          fullWidth
          size="small"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button
          variant="contained"
          onClick={() => onCreate(formData)}
          disabled={isCreating || !formData.name || !formData.code}
        >
          {isCreating ? t('common.creating') : t('common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Main Component ---

export function OrgUnitTreeView() {
  const { t } = useTranslation();
  const { data: orgUnits, isLoading, error, refetch } = useOrgUnits();
  const createMutation = useCreateOrgUnit();
  const updateMutation = useUpdateOrgUnit();
  const deleteMutation = useDeleteOrgUnit();
  const reorderMutation = useReorderOrgUnits();

  // Resizable tree panel — drag its right edge to widen so long unit names fit on one row.
  // Width persists in localStorage per route (same mechanism as the right-hand slide-in panels).
  const { width: treeWidth, ResizeHandle: TreeResizeHandle } = useResizablePanelWidth(
    'CoreOrgUnit-tree',
    { anchor: 'left', defaultWidth: 300, minWidth: 240 },
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createParentId, setCreateParentId] = useState<string | undefined>();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; orgUnitId: string } | null>(null);
  const [moveState, setMoveState] = useState<{ orgUnitId: string; toRoot: boolean } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<OrgUnit | null>(null);
  // Node switch with unsaved edits: the pending selection awaiting the ConfirmDialog ({ id }
  // wrapper so a pending "deselect" (null) is distinct from "no pending").
  const [pendingSelect, setPendingSelect] = useState<{ id: string | null } | null>(null);
  const [apiErrors, setApiErrors] = useState<ValidationError[]>([]);
  const [validationPanelOpen, setValidationPanelOpen] = useState(false);

  // Selected node's edit form — always editable; Save/Cancel live in the toolbar. The baseline (a
  // deep clone of the loaded node's payload) drives dirty-detection and Cancel. formDataNodeId
  // records which node the form currently holds, so we never render/compare it against a
  // just-switched node whose data hasn't loaded yet.
  const [formData, setFormData] = useState<UpdateOrgUnit | null>(null);
  const [formDataNodeId, setFormDataNodeId] = useState<string | null>(null);
  const originalFormDataRef = useRef<UpdateOrgUnit | null>(null);
  // Dimensions tab: the working {definitionId → valueId} map; null = untouched since load/save.
  // Saved together with the details form by the toolbar Save (resolve-on-save).
  const [dimensionValues, setDimensionValues] = useState<Record<string, string | null> | null>(null);
  const [dimensionResetKey, setDimensionResetKey] = useState(0);
  // Detail-panel tab, lifted to the page so it survives node switches (DetailPanel remounts per node).
  const [detailTabIndex, setDetailTabIndex] = useState(0);

  // Build tree from flat data
  const treeItems = useMemo(() => {
    if (!orgUnits) return [];
    return buildTree(orgUnits);
  }, [orgUnits]);

  // Find selected OrgUnit
  const selectedOrgUnit = useMemo(
    () => orgUnits?.find((ou) => ou.id === selectedId),
    [orgUnits, selectedId],
  );

  // Load the selected node into the form when the selection changes. Keyed on the node id (not the
  // node object) so a background refetch doesn't clobber in-progress edits; re-baselining after a
  // save is handled in handleSave's onSuccess. The payload is complete (buildOrgUnitUpdatePayload)
  // because the backend's update mapping is a total overwrite.
  useLayoutEffect(() => {
    if (selectedOrgUnit) {
      const fd = buildOrgUnitUpdatePayload(selectedOrgUnit);
      setFormData(fd);
      setFormDataNodeId(selectedOrgUnit.id);
      originalFormDataRef.current = JSON.parse(JSON.stringify(fd));
    } else {
      setFormData(null);
      setFormDataNodeId(null);
      originalFormDataRef.current = null;
    }
    setDimensionValues(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgUnit?.id]);

  // sortOrder is also written by the tree's Move up/Move down, and parentId by Move to… — both
  // renumber/re-parent server-side without changing the node id, so the load effect above does not
  // re-run, the form keeps the pre-move value, and because it still matches the baseline the page
  // does not even look dirty. The next Save then writes the stale value back and silently undoes
  // the move (the update payload is a total overwrite). Re-sync just these fields, baseline
  // included; other in-progress edits stay untouched, which is why the load stays keyed on the id.
  useLayoutEffect(() => {
    if (!selectedOrgUnit) return;
    const { sortOrder, parentId } = selectedOrgUnit;
    setFormData(prev => (prev && (prev.sortOrder !== sortOrder || prev.parentId !== parentId)
      ? { ...prev, sortOrder, parentId }
      : prev));
    if (originalFormDataRef.current) {
      originalFormDataRef.current.sortOrder = sortOrder;
      originalFormDataRef.current.parentId = parentId;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOrgUnit?.id, selectedOrgUnit?.sortOrder, selectedOrgUnit?.parentId]);

  // The form is "ready" only once it holds the currently-selected node's data. Between a node
  // switch and the load effect above, formData still belongs to the previous node — gating on this
  // prevents the detail panel from mounting with stale data and comparing it against the new baseline.
  const formReady = !!selectedOrgUnit && !!formData && formDataNodeId === selectedOrgUnit.id;
  const hasUnsavedChanges = formReady && !!originalFormDataRef.current
    && (JSON.stringify(formData) !== JSON.stringify(originalFormDataRef.current) || dimensionValues !== null);

  // Auto-expand root nodes on first load
  useMemo(() => {
    if (treeItems.length > 0 && expandedItems.length === 0) {
      setExpandedItems(treeItems.map((item) => item.id));
    }
  }, [treeItems.length]);

  // Guard node switches: with unsaved edits, confirm (via ConfirmDialog) before discarding them.
  // Selection is controlled, so declining keeps the current node.
  const selectNode = useCallback((itemId: string | null) => {
    if (itemId === selectedId) return;
    if (hasUnsavedChanges) {
      setPendingSelect({ id: itemId });
      return;
    }
    setApiErrors([]);
    setSelectedId(itemId);
  }, [hasUnsavedChanges, selectedId]);

  const confirmPendingSelect = useCallback(() => {
    if (pendingSelect) {
      setApiErrors([]);
      setSelectedId(pendingSelect.id);
    }
    setPendingSelect(null);
  }, [pendingSelect]);

  const handleAddChild = useCallback((parentId: string) => {
    setCreateParentId(parentId);
    setCreateDialogOpen(true);
  }, []);

  const handleAddRoot = useCallback(() => {
    setCreateParentId(undefined);
    setCreateDialogOpen(true);
  }, []);

  // Right-click deliberately does NOT change the selection: selectNode is guarded by the
  // unsaved-changes confirmation, and popping that dialog underneath a context menu is nonsense.
  // The menu therefore acts on the node that was clicked, whatever happens to be selected.
  const handleItemContextMenu = useCallback((event: React.MouseEvent, orgUnitId: string) => {
    event.preventDefault();
    event.stopPropagation();
    setContextMenu({ x: event.clientX + 2, y: event.clientY - 6, orgUnitId });
  }, []);

  // RichTreeView memoises each item on its slot props, so this object must keep a stable identity —
  // an inline one re-renders every node on every parent render.
  const treeItemSlotProps = useMemo(() => ({
    item: ({ itemId }: { itemId: string }) => ({
      // Enter must SELECT the focused node, whatever it is. MUI's built-in handler only selects
      // leaves — on an expandable item it toggles expansion instead, so keyboard users could never
      // open a parent's details. Setting defaultMuiPrevented makes useTreeItem skip that handler
      // (external handlers run first); ArrowRight/ArrowLeft still expand/collapse.
      onKeyDown: (event: React.KeyboardEvent & { defaultMuiPrevented?: boolean }) => {
        if (event.key !== 'Enter' || event.target !== event.currentTarget) return;
        event.defaultMuiPrevented = true;
        event.preventDefault();
        selectNode(itemId);
      },
      // `content` is the row itself and does not wrap the children group, so a right-click on a
      // child never also fires for its ancestors.
      slotProps: {
        content: { onContextMenu: (event: React.MouseEvent) => handleItemContextMenu(event, itemId) },
      },
    }),
  }), [handleItemContextMenu, selectNode]);

  const openMoveDialog = useCallback((orgUnitId: string, toRoot = false) => {
    setContextMenu(null);
    setMoveState({ orgUnitId, toRoot });
  }, []);

  const handleCreate = useCallback((data: CreateOrgUnit) => {
    setApiErrors([]);
    createMutation.mutate(data, {
      onSuccess: (created) => {
        setCreateDialogOpen(false);
        setSelectedId(created.id);
        // Expand parent so new child is visible
        if (data.parentId && !expandedItems.includes(data.parentId)) {
          setExpandedItems((prev) => [...prev, data.parentId!]);
        }
      },
      onError: (err: unknown) => {
        const errors = getValidationErrors(err);
        if (errors.length > 0) {
          setApiErrors(errors);
        } else {
          setApiErrors([{ field: 'form', message: t('orgUnit.createError'), instancePath: '#', members: [] }]);
        }
      },
    });
  }, [createMutation, expandedItems]);

  // Toolbar Save: details edits and staged dimension picks go out together. A touched Dimensions
  // tab is first resolved to a deduplicated DimensionSet id (Master-Data-Dimensions, Part 2 —
  // mirrors the generated detail-view save); an untouched editor never re-resolves, and the FK is
  // then read from the fresh server state, not the form baseline.
  const handleSave = useCallback(async () => {
    if (!selectedOrgUnit || !formData) return;
    setApiErrors([]);
    let defaultDimensionSetId = selectedOrgUnit.defaultDimensionSetId;
    if (dimensionValues !== null) {
      try {
        const pairs = Object.entries(dimensionValues)
          .filter(([, dimensionValueId]) => !!dimensionValueId)
          .map(([dimensionDefinitionId, dimensionValueId]) => ({ dimensionDefinitionId, dimensionValueId }));
        const res = await apiClient.post('/api/finance/dimension-set/resolve', { values: pairs });
        defaultDimensionSetId = res.data?.dimensionSetId ?? undefined;
      } catch {
        setApiErrors([{
          field: 'form',
          message: t('orgUnit.dimensionsSaveError', { defaultValue: 'Failed to save dimensions' }),
          instancePath: '#',
          members: [],
        }]);
        return;
      }
    }
    const data: UpdateOrgUnit = { ...formData, defaultDimensionSetId };
    updateMutation.mutate({ id: selectedOrgUnit.id, data }, {
      onSuccess: () => {
        setFormData(data);
        originalFormDataRef.current = JSON.parse(JSON.stringify(data));
        setDimensionValues(null);
      },
      onError: (err: unknown) => {
        const errors = getValidationErrors(err);
        if (errors.length > 0) {
          setApiErrors(errors);
        } else {
          setApiErrors([{ field: 'form', message: t('orgUnit.updateError'), instancePath: '#', members: [] }]);
        }
      },
    });
  }, [selectedOrgUnit, formData, dimensionValues, updateMutation, t]);

  const handleCancel = useCallback(() => {
    if (originalFormDataRef.current) {
      setFormData(JSON.parse(JSON.stringify(originalFormDataRef.current)));
    }
    // The dimension editor stages picks internally — remount it to discard them.
    setDimensionValues(null);
    setDimensionResetKey((k) => k + 1);
    setApiErrors([]);
  }, []);

  // A move is an ordinary update with a different parentId — but the payload has to be complete,
  // because the backend's update mapping overwrites every column it is given.
  const handleMove = useCallback((orgUnit: OrgUnit, newParentId: string | undefined) => {
    setApiErrors([]);
    updateMutation.mutate(
      { id: orgUnit.id, data: buildOrgUnitUpdatePayload(orgUnit, { parentId: newParentId }) },
      {
        onSuccess: () => {
          setMoveState(null);
          // Expand the new parent so the moved subtree is visible where it landed.
          if (newParentId && !expandedItems.includes(newParentId)) {
            setExpandedItems((prev) => [...prev, newParentId]);
          }
        },
        onError: (err: unknown) => {
          // Stay open so the user can pick a different parent; the banner explains why.
          const errors = getValidationErrors(err);
          if (errors.length > 0) {
            setApiErrors(errors);
          } else {
            setApiErrors([{ field: 'form', message: t('orgUnit.moveError'), instancePath: '#', members: [] }]);
          }
        },
      },
    );
  }, [updateMutation, expandedItems, t]);

  // Move a node one step among its siblings. The whole group goes to the server in its new order
  // and the server renumbers it in one unit of work — a failure part-way through can't leave the
  // group half-renumbered, and one click costs one audit record instead of one per sibling.
  //
  // The isPending guard matters beyond disabling the menu items: the mutation stays pending until
  // the invalidated list has actually refetched (see useReorderOrgUnits), so a second click can
  // never recompute the swap from the pre-reorder cache and appear to do nothing.
  const handleReorder = useCallback((orgUnitId: string, direction: -1 | 1) => {
    setContextMenu(null);
    if (reorderMutation.isPending) return;
    const target = orgUnits?.find((ou) => ou.id === orgUnitId);
    if (!orgUnits || !target) return;

    const siblings = getSiblingsInOrder(orgUnits, target);
    const index = siblings.findIndex((ou) => ou.id === orgUnitId);
    const neighborIndex = index + direction;
    if (index < 0 || neighborIndex < 0 || neighborIndex >= siblings.length) return;

    const reordered = [...siblings];
    [reordered[index], reordered[neighborIndex]] = [reordered[neighborIndex], reordered[index]];

    setApiErrors([]);
    reorderMutation.mutate(reordered.map((ou) => ou.id), {
      onError: (err: unknown) => {
        const errors = getValidationErrors(err);
        if (errors.length > 0) {
          setApiErrors(errors);
        } else {
          setApiErrors([{ field: 'form', message: t('orgUnit.updateError'), instancePath: '#', members: [] }]);
        }
      },
    });
  }, [orgUnits, reorderMutation, t]);

  const handleDelete = useCallback((id: string) => {
    const ou = orgUnits?.find((o) => o.id === id);
    if (!ou) return;
    setDeleteTarget(ou);
  }, [orgUnits]);

  const confirmDelete = useCallback(() => {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setApiErrors([]);
    deleteMutation.mutate(id, {
      onSuccess: () => {
        setDeleteTarget(null);
        if (selectedId === id) setSelectedId(null);
      },
      onError: (err: unknown) => {
        setDeleteTarget(null);
        const errors = getValidationErrors(err);
        if (errors.length > 0) {
          setApiErrors(errors);
          return;
        }
        // The delete service flattens the Axios error to a plain Error whose message is the
        // localized server text — e.g. the deletion guard's "still has sub-units" / "data is
        // still registered" explanation — so show that, not a canned guess at the cause.
        const message = err instanceof Error && err.message ? err.message : t('orgUnit.deleteError');
        setApiErrors([{ field: 'form', message, instancePath: '#', members: [] }]);
      },
    });
  }, [deleteTarget, deleteMutation, selectedId, t]);

  const createParentName = useMemo(
    () => orgUnits?.find((ou) => ou.id === createParentId)?.name,
    [orgUnits, createParentId],
  );

  const moveOrgUnit = useMemo(
    () => (moveState ? orgUnits?.find((ou) => ou.id === moveState.orgUnitId) : undefined),
    [orgUnits, moveState],
  );

  // Position of the context-menu target within its sibling group, for the up/down items.
  // null when the position is unknown, which disables both directions. `index <= 0` would have
  // folded "not found" (-1) into "is first", leaving Move DOWN enabled on a node handleReorder
  // refuses to move — and the swap would index reordered[-1].
  const contextMenuSiblingPosition = useMemo(() => {
    if (!contextMenu || !orgUnits) return null;
    const target = orgUnits.find((ou) => ou.id === contextMenu.orgUnitId);
    if (!target) return null;
    const siblings = getSiblingsInOrder(orgUnits, target);
    const index = siblings.findIndex((ou) => ou.id === target.id);
    if (index < 0) return null;
    return { isFirst: index === 0, isLast: index === siblings.length - 1 };
  }, [contextMenu, orgUnits]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{t('orgUnit.loadError')}: {error.message}</Alert>;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)' }}>
      {/* Button order mirrors the generated hierarchy admin page (and the standard DetailView
          toolbar): Cancel, Save, then the rest. All buttons are always visible; node-dependent
          ones disable without a selection. */}
      <ViewToolbar onRefresh={() => refetch()} hasUnsavedChanges={hasUnsavedChanges} onSave={handleSave} saveDisabled={updateMutation.isPending || !hasUnsavedChanges} onCancel={handleCancel} cancelDisabled={updateMutation.isPending || !hasUnsavedChanges}>
        <Tooltip title={cancelShortcutLabel()}>
          <span>
            <Button
              variant="outlined"
              size="small"
              onClick={handleCancel}
              disabled={updateMutation.isPending || !hasUnsavedChanges}
            >
              {t('common.cancel')}
            </Button>
          </span>
        </Tooltip>
        <Tooltip title={saveShortcutLabel()}>
          <span>
            <Button
              variant="outlined"
              size="small"
              onClick={handleSave}
              disabled={updateMutation.isPending || !hasUnsavedChanges}
            >
              {updateMutation.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </span>
        </Tooltip>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Delete />}
          disabled={!selectedOrgUnit}
          onClick={() => selectedOrgUnit && handleDelete(selectedOrgUnit.id)}
        >
          {t('common.delete')}
        </Button>
        <Button variant="outlined" size="small" startIcon={<Add />} onClick={handleAddRoot}>
          {t('orgUnit.addRoot')}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<Add />}
          disabled={!selectedOrgUnit}
          onClick={() => selectedOrgUnit && handleAddChild(selectedOrgUnit.id)}
        >
          {t('orgUnit.addChild')}
        </Button>
        <Button
          variant="outlined"
          size="small"
          startIcon={<DriveFileMove />}
          disabled={!selectedOrgUnit}
          onClick={() => selectedOrgUnit && openMoveDialog(selectedOrgUnit.id)}
        >
          {t('orgUnit.moveTo')}
        </Button>
      </ViewToolbar>

      <ValidationErrorBanner
        errors={apiErrors}
        onExpand={() => setValidationPanelOpen(true)}
        onClear={() => setApiErrors([])}
      />

      {/* Row flex: content on the left, the validation panel docked as a slide-out on the RIGHT —
          the house pattern from the detail/list views. Its collapsed 24px toggle strip only lays
          out correctly as a row flex sibling of the content. */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', px: 2, pb: 2 }}>
        <Typography variant="h5" sx={{ mb: 1, fontWeight: 500 }}>
          {t('orgUnit.organization')}
        </Typography>

      <Box sx={{ display: 'flex', flex: 1, gap: 2, overflow: 'hidden' }}>
        {/* Tree Panel (resizable — drag the right edge to widen; width persists in localStorage) */}
        <Box sx={{ position: 'relative', display: 'flex', width: treeWidth, minWidth: 240, flexShrink: 0 }}>
          <Paper
            variant="outlined"
            sx={{ flex: 1, overflow: 'auto' }}
          >
            {treeItems.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography color="text.secondary" gutterBottom>
                  {t('orgUnit.noUnitsYet')}
                </Typography>
                <Button variant="outlined" startIcon={<Add />} onClick={handleAddRoot}>
                  {t('orgUnit.createFirstUnit')}
                </Button>
              </Box>
            ) : (
              <RichTreeView
                items={treeItems}
                selectedItems={selectedId}
                onSelectedItemsChange={(_, itemId) => selectNode(itemId)}
                expandedItems={expandedItems}
                onExpandedItemsChange={(_, ids) => setExpandedItems(ids)}
                expansionTrigger="iconContainer"
                onItemClick={(event, itemId) => {
                  // Single click selects and leaves the subtree as it is; the chevron toggles
                  // expansion (expansionTrigger). Double-clicking the row toggles it too.
                  if (event.detail === 2) {
                    setExpandedItems((prev) =>
                      prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId],
                    );
                  }
                }}
                slots={{ item: CustomTreeItem }}
                slotProps={treeItemSlotProps}
                sx={{ py: 1 }}
              />
            )}
          </Paper>
          <TreeResizeHandle />
        </Box>

        {/* Detail Panel */}
        <Paper
          variant="outlined"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {selectedOrgUnit && formData && formDataNodeId === selectedOrgUnit.id ? (
            <DetailPanel
              key={selectedOrgUnit.id}
              orgUnit={selectedOrgUnit}
              formData={formData}
              setFormData={setFormData}
              tabIndex={detailTabIndex}
              setTabIndex={setDetailTabIndex}
              dimensionResetKey={dimensionResetKey}
              onDimensionChange={setDimensionValues}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <Typography color="text.secondary">
                {t('orgUnit.selectFromTree')}
              </Typography>
            </Box>
          )}
        </Paper>
      </Box>
      </Box>

        <ValidationErrorPanel
          errors={apiErrors}
          open={validationPanelOpen}
          onClose={() => setValidationPanelOpen(false)}
        />
      </Box>

      <Menu
        open={contextMenu !== null}
        onClose={() => setContextMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={contextMenu ? { top: contextMenu.y, left: contextMenu.x } : undefined}
      >
        <MenuItem onClick={() => contextMenu && handleAddChild(contextMenu.orgUnitId)}>
          <ListItemIcon><Add fontSize="small" /></ListItemIcon>
          <ListItemText>{t('orgUnit.addChild')}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => contextMenu && openMoveDialog(contextMenu.orgUnitId)}>
          <ListItemIcon><DriveFileMove fontSize="small" /></ListItemIcon>
          <ListItemText>{t('orgUnit.moveTo')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => contextMenu && openMoveDialog(contextMenu.orgUnitId, true)}
          disabled={!contextMenu || !orgUnits?.find((ou) => ou.id === contextMenu.orgUnitId)?.parentId}
        >
          <ListItemIcon><AccountTree fontSize="small" /></ListItemIcon>
          <ListItemText>{t('orgUnit.moveToRoot')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => contextMenu && handleReorder(contextMenu.orgUnitId, -1)}
          disabled={!contextMenuSiblingPosition || contextMenuSiblingPosition.isFirst || reorderMutation.isPending}
        >
          <ListItemIcon><ArrowUpward fontSize="small" /></ListItemIcon>
          <ListItemText>{t('orgUnit.moveUp')}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => contextMenu && handleReorder(contextMenu.orgUnitId, 1)}
          disabled={!contextMenuSiblingPosition || contextMenuSiblingPosition.isLast || reorderMutation.isPending}
        >
          <ListItemIcon><ArrowDownward fontSize="small" /></ListItemIcon>
          <ListItemText>{t('orgUnit.moveDown')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (!contextMenu) return;
            const id = contextMenu.orgUnitId;
            setContextMenu(null);
            handleDelete(id);
          }}
        >
          <ListItemIcon><Delete fontSize="small" color="error" /></ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>{t('common.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {moveOrgUnit && (
        <MoveOrgUnitDialog
          open
          orgUnit={moveOrgUnit}
          orgUnits={orgUnits ?? []}
          defaultToRoot={moveState?.toRoot}
          onClose={() => setMoveState(null)}
          onMove={handleMove}
          isMoving={updateMutation.isPending}
        />
      )}

      <CreateOrgUnitDialog
        open={createDialogOpen}
        parentId={createParentId}
        parentName={createParentName}
        onClose={() => setCreateDialogOpen(false)}
        onCreate={handleCreate}
        isCreating={createMutation.isPending}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        title={t('common.delete')}
        message={t('orgUnit.deleteConfirm', { name: deleteTarget?.name ?? '' })}
        confirmLabel={t('common.delete')}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
        confirmDisabled={deleteMutation.isPending}
      />
      <ConfirmDialog
        open={pendingSelect !== null}
        title={t('common.discardChanges')}
        message={t('common.discardChangesMessage')}
        confirmLabel={t('common.discard')}
        cancelLabel={t('common.keepEditing')}
        onConfirm={confirmPendingSelect}
        onCancel={() => setPendingSelect(null)}
      />
    </Box>
  );
}

// --- Custom Tree Item with type icons ---

function CustomTreeItem(props: any) {
  const { itemId, label, children, ...other } = props;

  return (
    <TreeItem
      {...other}
      itemId={itemId}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', py: 0.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 'inherit' }}>
            {label}
          </Typography>
        </Box>
      }
    >
      {children}
    </TreeItem>
  );
}
